/* eslint-disable max-lines */
/* eslint-disable no-param-reassign */
import { api } from "jiffy-api";
import { defineStore } from "pinia";
// eslint-disable-next-line import/no-cycle
import { useDtfStore } from "./store";

export const SPANGLE_MODES = {
  spangleIt: "spangle-it",
  editSize: "edit-size",
  sizeEdited: "size-edited",
  spanglesLoading: "spangles-loading"
};

const lastSpangledDimensionsDefault = { width: null, height: null };
const defaultVariation = { isLoading: true };

const initialState = {
  lastSpangledDimensions: lastSpangledDimensionsDefault,
  spangleVariations: [],
  spangleMode: SPANGLE_MODES.spangleIt,
  selectedAlgorithm: null,
  originalArtworkUrl: "",
  spanglesMaxImageCount: 1,
  allAPIsCompletedState: true,
  invalidSpangleSelected: false,
  isSpangleReordering: false,
  completedSpangleData: []
};

export const useSpanglesStore = defineStore("spangles", {
  state() {
    return { ...initialState };
  },
  getters: {
    spangleIt() {
      return this.spangleMode === SPANGLE_MODES.spangleIt;
    },
    spangleSizeEditing() {
      return this.spangleMode === SPANGLE_MODES.editSize;
    },
    spangleSizeEdited() {
      return this.spangleMode === SPANGLE_MODES.sizeEdited;
    },
    spanglesLoading() {
      return this.spangleMode === SPANGLE_MODES.spanglesLoading;
    },
    isInitialSpangle() {
      return this.spangleVariations.length === 0;
    },
    allAPIsCompleted() {
      return this.spangleVariations.every(variation => !variation.isLoading);
    },
    allAPIsFailed() {
      return this.spangleVariations.every(variation => !variation.isLoading && variation.error);
    },
    isSelectedVersionLoading() {
      const selectedVersion = this.spangleVariations.find(
        variation => variation.algorithm === this.selectedAlgorithm
      );
      return selectedVersion?.isLoading;
    },
    selectedVersion() {
      return this.spangleVariations.find(
        variation => variation.algorithm === this.selectedAlgorithm
      );
    },
    isFirstSpangle() {
      return this.spangleVariations.length === 0;
    }
  },
  actions: {
    setSpangleMode(mode) {
      this.spangleMode = mode;
    },
    setSpangleVariations(variations) {
      this.spangleVariations = variations;
    },
    setLastSpangledDimensions(dimensions) {
      this.lastSpangledDimensions = { ...dimensions };
    },
    setSelectedAlgorithm(algorithm) {
      this.selectedAlgorithm = algorithm;
    },
    setOriginalArtworkUrl(url) {
      this.originalArtworkUrl = url;
    },
    setInvalidSpangleSelected(invalid) {
      this.invalidSpangleSelected = invalid;
    },
    setIsSpangleReordering(isSpangleReordering) {
      this.isSpangleReordering = isSpangleReordering;
    },
    resetSpangles() {
      this.$reset();
    },
    addCompletedSpangleData(data) {
      this.completedSpangleData.push(data);
    },
    resetCompletedSpangleData() {
      this.completedSpangleData = [];
    },
    selectVersion({ version }) {
      if (!version) return;
      const store = useDtfStore();
      this.setSelectedAlgorithm(version.algorithm);

      if (version.isLoading) return;

      this.setInvalidSpangleSelected(version.error);
      const imageUrl = version.error ? this.originalArtworkUrl : version.imageUrl;

      store.updateArtworkValues({
        index: 0,
        spangleCsv: version.spangleCsv,
        spanglePagesInfo: version.spanglePagesInfo,
        spanglePositionsJson: version.positionsJson,
        spangledImageUrl: imageUrl
      });
    },
    prepareVariations({ isRetry }) {
      const {
        spangleVariations,
        selectedAlgorithm,
        allAPIsFailed,
        isFirstSpangle,
        isSpangleReordering
      } = this;
      const { spangleAlgorithms } = useDtfStore();

      let variationsObject = [];

      if (isRetry) {
        variationsObject = spangleVariations.map(variation => {
          if (variation.algorithm === selectedAlgorithm || allAPIsFailed) {
            return {
              ...defaultVariation,
              algorithm: variation.algorithm,
              spanglePagesInfo: variation.spanglePagesInfo
            };
          }
          return variation;
        });
      } else {
        const isFreshCall = isFirstSpangle || isSpangleReordering;
        const algorithmList = isFreshCall ? spangleAlgorithms : spangleVariations;
        variationsObject = algorithmList.map(item => ({
          ...(isFreshCall ? defaultVariation : item),
          algorithm: isFreshCall ? item : item.algorithm,
          spanglePagesInfo: isFreshCall ? null : item.spanglePagesInfo,
          isLoading: true
        }));
      }

      if (!selectedAlgorithm || isSpangleReordering) {
        this.setSelectedAlgorithm(variationsObject[0].algorithm);
      }

      this.setSpangleVariations(variationsObject);
    },
    updateSpangleVersion({ spangle }) {
      const { spangleVariations } = this;

      const updatedList = spangleVariations.map(variation =>
        variation.algorithm === spangle.algorithm
          ? { ...variation, ...spangle, isLoading: false }
          : variation
      );

      this.setSpangleVariations(updatedList);
    },
    // eslint-disable-next-line max-lines-per-function
    changeColor({ colorValues }) {
      const variationToUpdate = this.spangleVariations.filter(
        ({ isLoading, error, spanglePagesInfo }) => {
          const variationColorCount = spanglePagesInfo
            ? Object.values(spanglePagesInfo).filter(color => typeof color === "object").length
            : 0;

          const colorValuesCount = Object.keys(colorValues).length;

          return !isLoading && !error && colorValuesCount === variationColorCount;
        }
      );

      if (variationToUpdate.length === 0) return;

      /* eslint-disable camelcase */
      // eslint-disable-next-line max-lines-per-function
      variationToUpdate.map(variation => {
        const { total_beads_count, time_taken } = variation.spanglePagesInfo;

        return api.dtf
          .updateSpangleColor({
            positions_json: variation.positionsJson,
            pages_info: {
              ...colorValues,
              total_beads_count,
              time_taken
            }
          })
          .then(result => {
            const imageUrl = result.design_url || result.design_base64;

            this.updateSpangleVersion({
              spangle: {
                ...variation,
                imageUrl,
                spanglePagesInfo: result.pages_info,
                positionsJson: result.positions_json,
                error: false
              }
            });

            if (variation.algorithm === this.selectedAlgorithm) {
              const store = useDtfStore();
              store.updateArtworkValues({
                index: 0,
                spangled: true,
                spangledImageUrl: imageUrl,
                spanglePagesInfo: result.pages_info,
                spanglePositionsJson: result.positions_json
              });
            }
          })
          .catch(error => {
            window.Honeybadger?.notify("DTF spangle color change error", {
              params: {
                error_message: error.message,
                algorithm: variation.algorithm,
                pages_info: colorValues
              }
            });
          });
      });
      /* eslint-enable camelcase */
    },
    getVariation({
      algorithm,
      spanglePagesInfo,
      id,
      algorithmsSize,
      algorithmToAnimate,
      processTimeStamp
    }) {
      const { artworks, cable } = useDtfStore();

      /* eslint-disable no-underscore-dangle */
      cable.spangle({
        id,
        version_identifier: algorithm,
        pages_info: spanglePagesInfo,
        algorithms_size: algorithmsSize,
        algorithm_to_animate: algorithmToAnimate,
        process_time_stamp: processTimeStamp,
        width: artworks[0].width,
        height: artworks[0].height
      });
      /* eslint-enable no-underscore-dangle */
    },
    /* eslint-enable max-lines-per-function */
    getSpangleVariations({ isRetry, processTimeStamp }) {
      this.resetCompletedSpangleData();
      const { artworks, spangleAlgorithms } = useDtfStore();

      if (!spangleAlgorithms.length) {
        return;
      }

      const { id } = artworks[0];

      this.prepareVariations({ isRetry });

      this.setSpangleMode(SPANGLE_MODES.spanglesLoading);
      this.setInvalidSpangleSelected(false);
      this.setIsSpangleReordering(false);

      const algorithmsToCall = this.spangleVariations.filter(({ isLoading }) => isLoading);

      algorithmsToCall.map(({ algorithm, spanglePagesInfo }) =>
        this.getVariation({
          algorithm,
          spanglePagesInfo,
          id,
          algorithmsSize: algorithmsToCall.length,
          algorithmToAnimate: algorithmsToCall[0].algorithm,
          processTimeStamp
        })
      );
    },
    showSpangleResult() {
      const { artworks, updateArtworkValues } = useDtfStore();

      this.setLastSpangledDimensions({
        width: artworks[0]?.width,
        height: artworks[0]?.height
      });

      this.completedSpangleData.forEach(data => {
        this.updateSpangleVersion({ spangle: data });
      });

      // We want to select the first successful spangled algorithm
      const firstSuccessfuleSpangle = this.spangleVariations.find(data => !data.error);
      this.selectVersion({ version: firstSuccessfuleSpangle });

      updateArtworkValues({ index: 0, isLoading: false });
    },
    cancelSpangleProcess({ artwork }) {
      const { lastSpangledDimensions, spangleVariations, updateArtworkValuesById } = useDtfStore();
      const updatedList = spangleVariations.map(variation => ({ ...variation, isLoading: false }));

      this.setSpangleVariations(updatedList);
      updateArtworkValuesById({
        id: artwork.id,
        width: parseFloat(lastSpangledDimensions.width),
        height: parseFloat(lastSpangledDimensions.height)
      });

      this.setSpangleMode(SPANGLE_MODES.sizeEdited);
      this.resetCompletedSpangleData();
    },
    async cancelArtworkProcess({ artwork }) {
      api.dtf.cancelArtworkProcess({ id: artwork.id });
      this.setSpangleMode(SPANGLE_MODES.sizeEdited);
      this.resetCompletedSpangleData();
    }
  }
});
